import {ChangeEvent, FunctionComponent, HTMLAttributes, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import TrashCanIcon from '@/assets/images/icon-trash-can.svg?react';
import Button from '@/components/button';
import ConfirmDialog from '@/components/confirm-dialog';
import {useConfirmDialogActions} from '@/components/confirm-dialog/use-confirm-dialog-actions';
import {useDocumentLocale} from '@/components/document/hooks';
import Spinner from '@/components/spinner';
import {useAddDocumentAttachment, useRemoveDocumentAttachment} from '@/hooks/queries/documents';
import {Toaster} from '@/initializers/toaster';
import {DocumentAttachment, DocumentAttachmentType} from '@/interfaces/document';
import {downloadAttachment} from '@/repositories/beneva-api/endpoints/documents';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';
import {getFileExtension} from '@/utils/get-file-extension';
import Input from '../input';

const getMimeType = (attachment: DocumentAttachment) => {
  if (attachment.type === DocumentAttachmentType.image) {
    return `image/${attachment.extension}`;
  }

  if (attachment.type === DocumentAttachmentType.json) {
    return 'application/json';
  }

  if (attachment.type === DocumentAttachmentType.pdf) {
    return 'application/pdf';
  }

  return '';
};

const MAX_FILE_SIZE_IN_BYTES = 5 * 1024 * 1024; // 5 MB

type InputProps = Omit<HTMLAttributes<HTMLInputElement>, 'onChange'> & {
  documentId: string;
  dataTestId?: string;
  required?: boolean;
  disabled?: boolean;
  name?: string;
  id: string;
  value?: string;
  onChange: (attachmentId: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
};

const FileInput: FunctionComponent<InputProps> = ({
  documentId,
  dataTestId,
  required,
  disabled,
  name,
  id,
  value: attachmentId,
  onChange,
  onFocus,
  onBlur,
}) => {
  const {t} = useDocumentLocale();
  const addDocumentAttachment = useAddDocumentAttachment();
  const removeDocumentAttachment = useRemoveDocumentAttachment();

  const [preview, setPreview] = useState<string | undefined>();
  const [fileDescription, setFileDescription] = useState<{fileName: string; extension: string} | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isErrorDownload, setIsErrorDownload] = useState(false);
  const [isDeletingAttachment, setIsDeletingAttachment] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const hasFile = useMemo(() => {
    return preview || fileDescription;
  }, [preview, fileDescription]);

  useEffect(() => {
    async function run() {
      if (attachmentId) {
        setIsErrorDownload(false);
        setIsLoading(true);

        try {
          const {data: attachment} = await downloadAttachment(documentId, attachmentId);
          const mimeType = getMimeType(attachment);

          if (mimeType.startsWith('image')) {
            setPreview(`data:${mimeType};base64,${attachment.content}`);
          } else {
            setFileDescription({fileName: attachment.fileName, extension: attachment.extension});
          }
        } catch {
          setIsErrorDownload(true);
        } finally {
          setIsLoading(false);
        }
      } else {
        setPreview(undefined);
        setFileDescription(undefined);
        setIsLoading(false);
      }
    }

    run();
  }, [documentId, attachmentId]);

  const onHandleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files?.length) return;

      const file = event.target.files?.[0];
      if (!file) return;

      const fileType = file.type;
      let errorMessage;

      if (!fileType.startsWith('image/') && fileType !== 'application/pdf') {
        errorMessage = t('components.file-input.invalid-file-type-error');
      }

      if (file.size > MAX_FILE_SIZE_IN_BYTES) {
        errorMessage = t('components.file-input.max-file-size-error');
      }

      if (errorMessage) {
        Toaster.error(errorMessage);

        if (inputRef.current) {
          inputRef.current.value = '';
        }

        return;
      }

      setIsLoading(true);

      if (file.type.startsWith('image/')) {
        setPreview(URL.createObjectURL(file));
        setFileDescription(undefined);
      } else {
        setFileDescription({fileName: file.name, extension: getFileExtension(file.name)});
        setPreview(undefined);
      }

      try {
        if (attachmentId) {
          await removeDocumentAttachment.mutateAsync({documentId, attachmentId});
        }

        const newAttachmentId = await addDocumentAttachment.mutateAsync({
          documentId: documentId,
          fieldId: id,
          newAttachment: file,
        });

        onChange(newAttachmentId);
      } catch (_error) {
        setPreview(undefined);
        setFileDescription(undefined);
        Toaster.error(t('components.file-input.add-attachment-error'));
      } finally {
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        setIsLoading(false);
      }
    },
    [onChange, attachmentId, addDocumentAttachment, removeDocumentAttachment, documentId, id, t],
  );

  const startAttachmentDeletion = useCallback(() => {
    setIsDeletingAttachment(true);
  }, []);

  const cancelAttachmentDeletion = useCallback(() => {
    setIsDeletingAttachment(false);
  }, []);

  const confirmAttachmentDeletion = useCallback(async () => {
    if (!attachmentId) {
      setIsDeletingAttachment(false);
      return;
    }

    try {
      setIsErrorDownload(false);
      onChange('');

      if (inputRef.current) {
        inputRef.current.value = '';
      }

      await removeDocumentAttachment.mutateAsync({documentId, attachmentId});
    } catch (_error) {
      Toaster.error(t('components.file-input.remove-attachment-error'));
    } finally {
      setIsDeletingAttachment(false);
    }
  }, [attachmentId, removeDocumentAttachment, documentId, onChange, t]);

  const confirmDialogActions = useConfirmDialogActions({
    onCancel: cancelAttachmentDeletion,
    onConfirm: confirmAttachmentDeletion,
    confirmButtonLabel: t('components.file-input.remove-attachment-confirm.confirm'),
    cancelButtonLabel: t('components.file-input.remove-attachment-confirm.cancel'),
    confirmButtonTestId: 'file-input-confirm-dialog-confirm-button',
    cancelButtonTestId: 'file-input-confirm-dialog-cancel-button',
  });

  return (
    <div className="relative flex w-full flex-col gap-y-3">
      {hasFile && (
        <>
          {preview && (
            <>
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={preview}
                  alt=""
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform"
                />
              </div>

              {!isLoading && !disabled && (
                <Button
                  icon={<TrashCanIcon data-testid="file-input-remove-attachment-button" className="size-4" />}
                  className="absolute right-3 top-3 !size-10"
                  onClick={startAttachmentDeletion}
                  trackingKey={WEB_CLICK_TRACKING_KEY.DOWNLOAD_ATTACHMENT_ERROR}
                />
              )}
            </>
          )}

          {fileDescription && (
            <div className="flex h-16 w-full items-center justify-between gap-2 rounded-1 border border-charcoal p-3 text-base text-charcoal">
              <div className="rounded-1 bg-violet-200 px-2 py-1 text-xs font-bold text-violet">
                {/* eslint-disable i18next/no-literal-string */}.{fileDescription.extension}
                {/* eslint-enable i18next/no-literal-string */}
              </div>

              <div className="flex-grow overflow-hidden text-ellipsis text-nowrap">{fileDescription.fileName}</div>

              {!isLoading && !disabled && (
                <Button
                  icon={<TrashCanIcon className="size-4" />}
                  className="!size-10"
                  onClick={startAttachmentDeletion}
                  trackingKey={WEB_CLICK_TRACKING_KEY.DOWNLOAD_ATTACHMENT_ERROR}
                />
              )}
            </div>
          )}
        </>
      )}

      {isErrorDownload && (
        <div className="flex items-start justify-start gap-2 text-xs text-red">
          <Button
            icon={<TrashCanIcon className="size-7 text-red" />}
            className="!size-7 bg-red/10 hover:bg-red/20"
            onClick={startAttachmentDeletion}
            trackingKey={WEB_CLICK_TRACKING_KEY.DOWNLOAD_ATTACHMENT_ERROR}
          />
          {t('components.file-input.download-attachment-error')}
        </div>
      )}

      {isLoading && (
        <div className="absolute left-0 top-0 flex size-full items-center justify-center bg-white/60">
          <Spinner className="size-10" />
        </div>
      )}

      <Input
        type="file"
        accept="image/*,.pdf"
        ref={inputRef}
        onChange={onHandleChange}
        data-testid={dataTestId}
        required={required}
        disabled={disabled}
        name={name}
        id={id}
        onFocus={onFocus}
        onBlur={onBlur}
      />

      <ConfirmDialog
        isVisible={!!isDeletingAttachment}
        title={t('components.file-input.remove-attachment-confirm.title')}
        text={t('components.file-input.remove-attachment-confirm.text')}
        actions={confirmDialogActions}
      />
    </div>
  );
};

export default FileInput;
